import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import image1 from "../../Asset/image1.webp";
import image6 from "../../Asset/image6.webp";
import { 
    FaCheckCircle, FaUser, FaChevronDown, 
    FaBaby, FaWheelchair, FaCalculator, FaQuestionCircle, FaStar, FaArrowRight, FaFileAlt, FaMapMarkerAlt, FaTimesCircle, FaGoogle, FaCar, FaHome
} from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { useColorMode } from '@chakra-ui/react';
import { motion, AnimatePresence, useScroll, useTransform, useInView } from 'framer-motion';
import SafeContent from '../Securite/safeContent';

const CARD_GRADIENTS = [
    'from-blue-600 to-violet-700',
    'from-emerald-500 to-teal-800',
    'from-rose-500 to-pink-700',
    'from-amber-500 to-orange-700',
    'from-cyan-500 to-blue-800',
    'from-fuchsia-500 to-purple-800',
];

// --- COMPOSANT : FAQ INTERACTIVE ---
const FAQItem = ({ question, answer, isDark }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className={`mb-3 md:mb-4 rounded-xl md:rounded-2xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-50'} overflow-hidden transition-all duration-300 ${isOpen ? 'shadow-md shadow-blue-900/5' : ''}`}>
            <button className="w-full p-4 md:p-6 flex justify-between items-center text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)}>
                <span className={`text-base md:text-lg font-bold pr-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>{question}</span>
                <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }} className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700 text-yellow-400' : 'bg-blue-50 text-blue-600'}`}>
                    <FaChevronDown size={12} />
                </motion.div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                        <p className={`px-4 md:px-6 pb-4 md:pb-6 text-sm md:text-base font-medium leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{answer}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// --- COMPOSANT : COMPTEUR ANIMÉ GÉNÉRIQUE ---
const AnimatedCounter = ({ end, decimals = 0, duration = 2000 }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "0px 0px -80px 0px" });

    useEffect(() => {
        if (!isInView) return;
        const steps = 60;
        const increment = end / steps;
        const intervalTime = duration / steps;
        let current = 0;
        const timer = setInterval(() => {
            current = Math.min(current + increment, end);
            setCount(current);
            if (current >= end) clearInterval(timer);
        }, intervalTime);
        return () => clearInterval(timer);
    }, [isInView, end, duration]);

    return <span ref={ref}>{decimals > 0 ? count.toFixed(decimals) : Math.round(count)}</span>;
};

const FOUNDED_YEAR = 2007;
const yearsOfExperience = () => new Date().getFullYear() - FOUNDED_YEAR;

const ExperienceCounter = () => <AnimatedCounter end={yearsOfExperience()} duration={2000} />;

// --- COMPOSANT : VÉRIFICATEUR DE DISPONIBILITÉ ---
const AvailabilityChecker = ({ isDark }) => {
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState('idle'); // 'idle', 'success', 'error'

    const handleCheck = (e) => {
        e.preventDefault();
        if (!search) return;

        // Départements valides : 75, 92, 93, 95
        const regex = /\b(75|92|93|95)\d{3}\b|^(75|92|93|95)$/;
        
        if (regex.test(search.trim())) {
            setStatus('success');
        } else {
            setStatus('error');
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-6 relative z-40 -mt-8 md:-mt-12 mb-16">
            <div className={`p-6 md:p-8 rounded-[2rem] shadow-2xl border flex flex-col md:flex-row items-center gap-6 md:gap-8 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/95 backdrop-blur-xl border-white'}`}>
                <div className="flex-1 text-center md:text-left w-full">
                    <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                            <FaMapMarkerAlt size={16} />
                        </div>
                        <h3 className={`text-xl md:text-2xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                            Intervenons-nous chez vous ?
                        </h3>
                    </div>
                    <p className={`text-sm md:text-base ${isDark ? 'text-gray-400' : 'text-gray-500'} md:pl-14`}>
                        Vérifiez notre zone de couverture en saisissant votre code postal (ex: 93200) ou département.
                    </p>
                </div>

                <div className="w-full md:w-auto flex flex-col gap-3">
                    <form onSubmit={handleCheck} className="flex flex-col sm:flex-row items-center gap-3">
                        <input 
                            type="text" 
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setStatus('idle'); }}
                            placeholder="Code postal..." 
                            className={`w-full sm:w-56 px-5 py-4 rounded-xl border font-medium outline-none transition-all ${
                                isDark 
                                    ? 'bg-gray-900 border-gray-700 text-white focus:border-blue-500' 
                                    : 'bg-gray-50 border-gray-200 text-[#1B365D] focus:border-blue-400'
                            }`}
                        />
                        <button type="submit" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg shadow-blue-600/20 flex justify-center items-center">
                            Vérifier
                        </button>
                    </form>
                    
                    <AnimatePresence mode="wait">
                        {status === 'success' && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="flex items-center justify-center sm:justify-start gap-2 text-green-600 dark:text-green-400 text-sm font-bold">
                                <FaCheckCircle /> Excellente nouvelle ! Nos équipes interviennent dans votre secteur.
                            </motion.div>
                        )}
                        {status === 'error' && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="flex items-center justify-center sm:justify-start gap-2 text-red-500 dark:text-red-400 text-sm font-bold">
                                <FaTimesCircle /> Nous n'intervenons malheureusement pas encore dans ce secteur.
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

function Home() {
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';
    const [recentBlogs, setRecentBlogs] = useState([]);
    const { scrollYProgress, scrollY } = useScroll();
    const heroImageY = useTransform(scrollY, [0, 700], [0, -80]);

    const fetchRecentBlogs = async () => {
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
        try {
            const response = await axios.get(`${API_URL}/api/blog`);
            if (response.status === 200) {
                const sortedBlogs = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setRecentBlogs(sortedBlogs.slice(0, 2));
            }
        } catch (error) {}
    };

    useEffect(() => { fetchRecentBlogs(); }, []);

    const getBlogImageUrl = (imagePath) => {
        if (!imagePath) return null;
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
        if (imagePath.startsWith('http')) return imagePath;
        const fileName = imagePath.split('/').pop().split('\\').pop();
        return `${API_URL}/uploads/${fileName}`; 
    };

    const sectionVariants = { 
        hidden: { opacity: 0, y: 40 }, 
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } 
    };

    return (
        <div className={`overflow-hidden ${isDark ? 'bg-[#0a0f16]' : 'bg-[#FAFCFF]'}`}>
            <Helmet>
                <title>Hetep Iaout Services — Aide à domicile Île-de-France | Cap'Handéo</title>
                <meta name="description" content={`Aide à domicile certifiée Cap'Handéo : accompagnement senior, garde d'enfants, transport PMR, soutien handicap en Île-de-France. ${yearsOfExperience()} ans d'expérience. Devis gratuit 24h/7j.`} />
                <link rel="canonical" href="https://www.hisvie.com/" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.hisvie.com/" />
                <meta property="og:title" content="Hetep Iaout Services — Aide à domicile Île-de-France | Cap'Handéo" />
                <meta property="og:description" content={`Accompagnement senior, garde d'enfants, transport PMR, soutien handicap. Certifié Cap'Handéo. ${yearsOfExperience()} ans d'expérience. 500+ familles accompagnées.`} />
                <meta property="og:image" content="https://www.hisvie.com/logo192.png" />
                <script type="application/ld+json">{JSON.stringify({
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "LocalBusiness",
                            "@id": "https://www.hisvie.com/#organization",
                            "name": "Hetep Iaout Services",
                            "alternateName": "HIS",
                            "url": "https://www.hisvie.com",
                            "logo": "https://www.hisvie.com/logo192.png",
                            "telephone": "+33143526423",
                            "email": "his@donnerlavieauxannees.com",
                            "foundingDate": "2006",
                            "address": { "@type": "PostalAddress", "streetAddress": "8 place Georges Braque", "addressLocality": "La Courneuve", "postalCode": "93120", "addressCountry": "FR" },
                            "geo": { "@type": "GeoCoordinates", "latitude": 48.9310, "longitude": 2.3913 },
                            "areaServed": { "@type": "State", "name": "Île-de-France" },
                            "openingHours": "Mo-Su 00:00-23:59",
                            "hasOfferCatalog": {
                                "@type": "OfferCatalog",
                                "name": "Services d'aide à domicile",
                                "itemListElement": [
                                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Accompagnement senior et personnes âgées" } },
                                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Garde d'enfants à domicile" } },
                                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Transport PMR adapté" } },
                                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Accompagnement personnes handicapées" } },
                                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Entretien du domicile" } }
                                ]
                            },
                            "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "127", "bestRating": "5" },
                            "sameAs": ["https://www.facebook.com/his2015/", "https://www.instagram.com/hetepiaoutservices/", "https://www.linkedin.com/company/hetepiaoutservices/"]
                        },
                        {
                            "@type": "WebSite",
                            "@id": "https://www.hisvie.com/#website",
                            "url": "https://www.hisvie.com",
                            "name": "Hetep Iaout Services",
                            "publisher": { "@id": "https://www.hisvie.com/#organization" }
                        },
                        {
                            "@type": "FAQPage",
                            "mainEntity": [
                                { "@type": "Question", "name": "Puis-je bénéficier d'aides financières ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui, nos services donnent droit au crédit d'impôt de 50%. Selon la pathologie, vous pouvez bénéficier de l'APA ou de la PCH." } },
                                { "@type": "Question", "name": "Le personnel a-t-il une formation médicale ?", "acceptedAnswer": { "@type": "Answer", "text": "Nos intervenants possèdent des diplômes d'État (DEAVS, AMP) et suivent des formations continues sur les pathologies lourdes." } },
                                { "@type": "Question", "name": "Que se passe-t-il en cas d'absence d'un intervenant ?", "acceptedAnswer": { "@type": "Answer", "text": "La continuité de service est notre priorité absolue. Nous garantissons un remplacement immédiat." } }
                            ]
                        }
                    ]
                })}</script>
            </Helmet>

            <motion.div className="fixed top-0 left-0 right-0 h-1 bg-blue-600 origin-left z-[100]" style={{ scaleX: scrollYProgress }} />
            
            {/* --- HERO SECTION --- */}
            <div className="relative w-full min-h-[100vh] flex flex-col justify-end overflow-hidden">
                <motion.img
                    src={image1}
                    alt="Accompagnement à domicile"
                    className="absolute left-0 right-0 w-full object-cover object-center z-0"
                    style={{ y: heroImageY, height: '120%', top: '-10%' }}
                />

                {/* Gradient du bas vers le haut — laisse respirer la photo en haut */}
                <div className="absolute inset-0 z-10"
                    style={{ background: 'linear-gradient(to top, rgba(5,10,18,0.97) 0%, rgba(5,10,18,0.75) 45%, rgba(5,10,18,0.15) 100%)' }}
                />

                <div className="relative z-20 w-full max-w-5xl mx-auto px-5 md:px-8 pb-12 md:pb-20 pt-40">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: 'easeOut' }}>

                        {/* Indicateur live */}
                        <div className="flex items-center gap-2.5 mb-7">
                            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse shrink-0" />
                            <span className="text-white/50 text-[11px] font-bold uppercase tracking-[0.2em]">
                                Île-de-France · Disponible 24h/7j
                            </span>
                        </div>

                        {/* Titre */}
                        <h1 className="text-[2.8rem] sm:text-6xl md:text-[5.5rem] font-black leading-[1.0] tracking-tighter text-white mb-7">
                            Prendre soin<br />
                            de ceux que<br />
                            <span className="font-serif font-light italic text-blue-300">vous aimez.</span>
                        </h1>

                        {/* Description */}
                        <p className="text-base md:text-lg text-white/60 max-w-xl leading-relaxed mb-10 font-light">
                            Depuis {yearsOfExperience()} ans, nous accompagnons les familles d'Île-de-France face au handicap lourd, aux maladies rares et à la dépendance — avec une exigence médicale et humaine absolue.
                        </p>

                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row gap-3 mb-14">
                            <Link to="/demande-de-devis">
                                <button className="w-full sm:w-auto bg-white text-[#1B365D] font-black py-4 px-8 rounded-2xl hover:bg-blue-50 transition-all shadow-2xl shadow-black/30 text-sm tracking-wide">
                                    Évaluer mes besoins
                                </button>
                            </Link>
                            <Link to="/Presentation">
                                <button className="w-full sm:w-auto font-semibold py-4 px-8 rounded-2xl border border-white/20 text-white/80 hover:bg-white/8 transition-all text-sm tracking-wide">
                                    Découvrir l'agence
                                </button>
                            </Link>
                        </div>

                        {/* Bande de stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-8 pt-8 border-t border-white/10">
                            {[
                                { num: `${yearsOfExperience()} ans`, label: "d'expérience" },
                                { num: '500+',  label: 'familles accompagnées' },
                                { num: '4.8/5', label: 'avis clients Google' },
                                { num: "Cap'H", label: 'Certifié Cap\'Handéo' },
                            ].map(s => (
                                <div key={s.label}>
                                    <p className="text-xl md:text-2xl font-black text-white">{s.num}</p>
                                    <p className="text-[11px] text-white/40 mt-1 uppercase tracking-wide">{s.label}</p>
                                </div>
                            ))}
                        </div>

                    </motion.div>
                </div>
            </div>
            
            {/* --- VÉRIFICATEUR DE DISPONIBILITÉ --- */}
            <AvailabilityChecker isDark={isDark} />

           {/* --- NAVIGATION PAR PRESTATION --- */}
            <motion.div 
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
                className="max-w-4xl mx-auto px-4 md:px-6 pb-12 md:pb-20 relative z-30 text-center"
            >
                <h3 className={`text-2xl md:text-4xl font-black mb-3 md:mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                    De quel accompagnement avez-vous besoin ?
                </h3>
                <p className={`text-sm md:text-lg mb-10 md:mb-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Découvrez l'ensemble de nos prestations sur-mesure.
                </p>

                <div className="flex flex-wrap justify-center items-start gap-6 md:gap-12">
                    {[
                        { to: "/enfants", icon: <FaBaby size={28} />, title: "Garde\nd'enfants" },
                        { to: "/seniors", icon: <FaUser size={28} />, title: "Personnes\nâgées" },
                        { to: "/personnes-handicapees", icon: <FaWheelchair size={28} />, title: "Accompagnement\nhandicap" },
                        { to: "/transport-de-pmr", icon: <FaCar size={28} />, title: "Transport\nde PMR" },
                        { to: "/entretien-du-domicile", icon: <FaHome size={28} />, title: "Entretien\ndu domicile" }
                    ].map((service, index) => (
                        <Link key={index} to={service.to} className="group flex flex-col items-center w-24 md:w-28">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.08 }}
                                whileHover={{ scale: 1.08, y: -5 }}
                                whileTap={{ scale: 0.95 }}
                                className={`w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-colors duration-300 shadow-sm group-hover:shadow-xl border ${
                                    isDark
                                    ? 'bg-gray-800 border-gray-700 text-gray-400 group-hover:bg-gray-700 group-hover:text-white group-hover:border-gray-500'
                                    : 'bg-white border-gray-100 text-gray-400 group-hover:bg-[#1B365D] group-hover:text-white group-hover:border-[#1B365D]'
                                }`}
                            >
                                {service.icon}
                            </motion.div>
                            <motion.span
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.08 + 0.1 }}
                                className={`mt-4 text-xs md:text-sm font-bold text-center leading-snug transition-colors duration-300 whitespace-pre-line ${
                                    isDark
                                    ? 'text-gray-400 group-hover:text-white'
                                    : 'text-gray-500 group-hover:text-[#1B365D]'
                                }`}
                            >
                                {service.title}
                            </motion.span>
                        </Link>
                    ))}
                </div>
            </motion.div>

            {/* --- NOS CHIFFRES --- */}
            <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={sectionVariants}
                className="max-w-5xl mx-auto px-4 md:px-6 pb-12 md:pb-20"
            >
                <div className={`grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 rounded-[2rem] md:rounded-[2.5rem] overflow-hidden border ${isDark ? 'border-gray-800 divide-gray-800' : 'border-gray-100 divide-gray-100 shadow-xl shadow-blue-900/5'}`}>
                    {[
                        { num: yearsOfExperience(), suffix: ' ans', label: "d'expérience", decimals: 0 },
                        { num: 500, suffix: '+', label: 'familles accompagnées', decimals: 0 },
                        { num: 4.8, suffix: '/5', label: 'note Google', decimals: 1 },
                        { num: 24, suffix: 'h/7j', label: 'disponibilité', decimals: 0 },
                    ].map((stat, i) => (
                        <div key={i} className={`flex flex-col items-center justify-center text-center py-8 md:py-10 px-4 ${isDark ? 'bg-gray-900/60' : 'bg-white'}`}>
                            <p className={`text-3xl md:text-4xl font-black mb-1 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                <AnimatedCounter end={stat.num} decimals={stat.decimals} duration={1800} />
                                <span className="text-blue-600">{stat.suffix}</span>
                            </p>
                            <p className={`text-[11px] md:text-xs uppercase tracking-widest font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{stat.label}</p>
                        </div>
                    ))}
                </div>
            </motion.div>

            {/* --- FOCUS : 19 ANS D'EXPÉRIENCE --- */}
            <motion.div 
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants}
                className="max-w-6xl mx-auto px-4 md:px-6 pb-16 md:pb-24 relative z-20 mt-8 md:-mt-8"
            >
                <div className={`relative overflow-hidden p-10 md:p-16 rounded-[2rem] md:rounded-[3rem] shadow-2xl flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 border ${isDark ? 'bg-gray-900/95 border-gray-800 backdrop-blur-xl' : 'bg-white/95 border-white backdrop-blur-xl'}`}>
                    <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] pointer-events-none hidden md:block"></div>

                    <div className="relative z-10 flex items-baseline justify-center md:justify-end md:w-5/12">
                        <span className="text-7xl md:text-[9rem] font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-blue-600 to-blue-400 leading-none drop-shadow-sm">
                            <ExperienceCounter />
                        </span>
                        <span className={`text-3xl md:text-5xl font-black ml-2 md:ml-4 -mb-2 md:-mb-6 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                            ans.
                        </span>
                    </div>

                    <div className={`w-3/4 md:w-px h-px md:h-32 opacity-20 ${isDark ? 'bg-white' : 'bg-[#1B365D]'}`}></div>

                    <div className="relative z-10 text-center md:text-left md:w-7/12 max-w-lg">
                        <h3 className={`text-2xl md:text-4xl font-black mb-4 leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                            D'engagement <br className="hidden md:block"/>à vos côtés.
                        </h3>
                        <p className={`text-sm md:text-lg leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Depuis près de deux décennies, nous accompagnons les familles avec une exigence absolue. Un savoir-faire humain et médical forgé par le temps, pour vous offrir la plus grande des sérénités.
                        </p>
                    </div>
                </div>
            </motion.div>

           {/* --- SECTION EXPERTISE & SÉCURITÉ --- */}
            <motion.div 
                className="py-16 md:py-28 px-4 md:px-6 max-w-7xl mx-auto" 
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
            >
                <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-start">
                    
                    <div className="w-full lg:w-5/12 lg:sticky lg:top-32">
                        <span className="text-blue-600 font-bold tracking-widest uppercase text-xs md:text-sm mb-4 block">
                            Notre exigence
                        </span>
                        <h2 className={`text-3xl md:text-5xl font-black mb-6 leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                            Le confort de la maison. <br />
                            <span className="font-serif italic font-light text-blue-500">La rigueur d'une clinique.</span>
                        </h2>
                        <p className={`text-base md:text-lg mb-10 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Parce que la santé et la sécurité de vos proches ne tolèrent aucun compromis, nous avons bâti notre accompagnement sur 4 piliers d'excellence.
                        </p>
                        
                        <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border border-white/10 dark:border-gray-800 hidden md:block">
                            <img 
                                src={image6} 
                                alt="Expertise médicale" 
                                className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-700" 
                            />
                        </div>
                    </div>

                    <div className="w-full lg:w-7/12 flex flex-col gap-12 md:gap-16 pt-4 lg:pt-0">
                        <div className="relative group">
                            <div className="flex gap-6 items-start">
                                <div className="text-5xl md:text-6xl font-black text-blue-100 dark:text-gray-800 pt-1 group-hover:text-blue-600 transition-colors duration-300">
                                    01
                                </div>
                                <div>
                                    <h3 className={`text-xl md:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Protocoles Sanitaires Stricts</h3>
                                    <p className={`text-sm md:text-base leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Nos équipes sont formées aux gestes techniques les plus complexes. Nous appliquons des mesures d'hygiène rigoureuses, directement inspirées du milieu hospitalier, pour garantir un environnement stérile et sécurisé.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={`w-full h-px ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}></div>

                        <div className="relative group">
                            <div className="flex gap-6 items-start">
                                <div className="text-5xl md:text-6xl font-black text-blue-100 dark:text-gray-800 pt-1 group-hover:text-blue-600 transition-colors duration-300">
                                    02
                                </div>
                                <div>
                                    <h3 className={`text-xl md:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Astreinte & Intervention 24h/7j</h3>
                                    <p className={`text-sm md:text-base leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        L'urgence n'attend pas. Nos coordinateurs assurent une veille permanente pour déployer une assistance immédiate, modifier un planning ou répondre à vos inquiétudes, de jour comme de nuit.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={`w-full h-px ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}></div>

                        <div className="relative group">
                            <div className="flex gap-6 items-start">
                                <div className="text-5xl md:text-6xl font-black text-blue-100 dark:text-gray-800 pt-1 group-hover:text-blue-600 transition-colors duration-300">
                                    03
                                </div>
                                <div>
                                    <h3 className={`text-xl md:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Continuité Humaine Garantie</h3>
                                    <p className={`text-sm md:text-base leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        L'humain est au cœur de notre métier. Nous privilégions des binômes stables pour créer un vrai lien de confiance avec la famille. En cas d'imprévu, un remplaçant qualifié et déjà briefé prend le relais.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={`w-full h-px ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}></div>

                        <div className="relative group">
                            <div className="flex gap-6 items-start">
                                <div className="text-5xl md:text-6xl font-black text-blue-100 dark:text-gray-800 pt-1 group-hover:text-blue-600 transition-colors duration-300">
                                    04
                                </div>
                                <div>
                                    <h3 className={`text-xl md:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Zéro Charge Mentale</h3>
                                    <p className={`text-sm md:text-base leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Consacrez votre temps à vos proches, nous gérons le reste. De la mise en place du planning à la gestion de vos aides financières (APA, PCH) et du crédit d'impôt, notre équipe s'occupe de tout.
                                    </p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </motion.div>

            {/* --- SECTION ESTIMATION --- */}
            <motion.section 
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
                className="py-12 md:py-24 px-4 md:px-6"
            >
                <div className={`max-w-5xl mx-auto rounded-3xl md:rounded-[3rem] overflow-hidden flex flex-col md:flex-row items-center relative shadow-2xl border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-[#1B365D] border-[#1B365D]'}`}>
                    
                    <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400 rounded-full filter blur-[100px] opacity-20 pointer-events-none transform translate-x-1/3 -translate-y-1/3"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400 rounded-full filter blur-[100px] opacity-20 pointer-events-none transform -translate-x-1/3 translate-y-1/3"></div>
                    
                    <div className="p-8 md:p-16 flex-1 relative z-10 w-full text-center md:text-left">
                        <div className="flex items-center justify-center md:justify-start gap-2 mb-6 text-yellow-400">
                            <FaCalculator size={18} />
                            <span className="font-bold uppercase tracking-widest text-xs">Simulateur d'Aides</span>
                        </div>
                        <h2 className="text-3xl md:text-5xl font-black mb-6 leading-tight text-white">
                            Estimez votre <br className="hidden md:block"/>reste à charge.
                        </h2>
                        <p className="text-sm md:text-lg mb-10 leading-relaxed text-blue-100/80">
                            APA, PCH, Crédit d'Impôt... Découvrez instantanément vos aides financières.
                        </p>
                        
                        <Link to="/simulateur-aides" className="relative inline-block w-full md:w-auto">
                            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full md:w-auto px-10 py-5 bg-yellow-400 text-[#1B365D] font-black rounded-2xl shadow-lg shadow-yellow-400/20 flex items-center justify-center gap-3 hover:bg-yellow-300 transition-colors">
                                Démarrer l'évaluation <FaArrowRight />
                            </motion.button>
                        </Link>
                    </div>

                    <div className="flex flex-1 w-full relative min-h-[350px] items-center justify-center p-8 overflow-visible z-10">
                        <div className="relative transform scale-90 md:scale-110">
                            <motion.div 
                                animate={{ rotate: 360 }} 
                                transition={{ repeat: Infinity, duration: 25, ease: "linear" }}
                                className="absolute -inset-10 rounded-full border-2 border-dashed border-white/20 opacity-50 pointer-events-none"
                            />

                            <motion.div 
                                animate={{ y: [-10, 10, -10] }} 
                                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                                className="relative z-20 w-44 h-60 rounded-[2rem] shadow-2xl border p-4 backdrop-blur-md bg-white/10 border-white/20"
                            >
                                <div className="w-full h-12 rounded-xl mb-4 flex items-center justify-end p-3 shadow-inner bg-black/20">
                                    <span className="font-mono text-xl font-black text-yellow-400">€ 412,50</span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 flex-1">
                                    {[...Array(9)].map((_, i) => (
                                        <div key={i} className="rounded-lg h-8 bg-white/20"></div>
                                    ))}
                                    <div className="col-span-3 h-10 rounded-lg bg-blue-500 shadow-inner"></div>
                                </div>
                            </motion.div>

                            <motion.div 
                                animate={{ y: [10, -15, 10], x: [0, 15, 0] }} 
                                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                                className="absolute -top-6 -right-12 z-30 px-4 py-3 bg-white/10 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 flex items-center gap-3"
                            >
                                <div className="w-10 h-10 bg-green-400/20 text-green-400 rounded-xl flex items-center justify-center font-black">-50%</div>
                                <span className="text-xs font-bold whitespace-nowrap text-white">Crédit d'impôt</span>
                            </motion.div>

                            <motion.div 
                                animate={{ y: [-15, 10, -15], x: [10, -10, 10] }} 
                                transition={{ repeat: Infinity, duration: 7, ease: "easeInOut", delay: 1 }}
                                className="absolute -bottom-4 -left-12 z-30 px-4 py-3 bg-white/10 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 flex items-center gap-3"
                            >
                                <div className="w-10 h-10 bg-blue-400/20 text-blue-400 rounded-xl flex items-center justify-center"><FaCheckCircle size={18}/></div>
                                <span className="text-xs font-bold whitespace-nowrap text-white">Aides APA / PCH</span>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* --- SECTION AVIS GOOGLE --- */}
            <motion.section 
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
                className="py-12 md:py-24 px-4 md:px-6 max-w-7xl mx-auto"
            >
                <div className="flex flex-col md:flex-row justify-between items-end mb-10 md:mb-16 gap-6">
                    <div className="text-center md:text-left">
                        <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                            <FaGoogle className="text-blue-600 text-xl md:text-2xl" />
                            <h2 className={`text-2xl md:text-4xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                Ils nous font <span className="text-blue-600">confiance</span>
                            </h2>
                        </div>
                        <p className={`text-sm md:text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Découvrez les retours des familles que nous accompagnons au quotidien.
                        </p>
                    </div>
                    
                    <div className={`flex items-center gap-4 px-6 py-3 rounded-2xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white shadow-lg border-gray-50'}`}>
                        <div className="flex flex-col">
                            <span className={`text-xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>4.8/5</span>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Avis certifiés</span>
                        </div>
                        <div className="flex text-yellow-400">
                            <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                        </div>
                    </div>
                </div>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { id: 1, name: "Sophia Jaai", date: "Avis vérifié", rating: 5, url: "https://www.google.com/search?sa=X&sca_esv=4f696a57d58ffefe&sxsrf=ANbL-n7sBp4ZTXSX82B62xOwcomS4fuxtg:1777452283109&q=Hetep-Iaout+Services+Avis&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxIxNDUyMjcyMjC1MDAwMDczMjOysNzAyPiKUdIjtSS1QNczMb-0RCE4tagsMzm1WMGxLLN4EStuOQB3JQ-cUwAAAA&rldimm=15227220580007626289&tbm=lcl&hl=fr-FR&ved=2ahUKEwiamMLf1ZKUAxVxUqQEHSn1PAkQ9fQKegQIRBAG&biw=1536&bih=695&dpr=1.25#lkt=LocalPoiReviews", text: "Une agence exceptionnelle ! Après plusieurs expériences décevantes, j'ai enfin trouvé une équipe qui s'adapte parfaitement aux besoins spécifiques du père de mes enfants. Ils ont réussi à respecter toutes mes demandes, même les plus complexes... Je recommande vivement cette agence pour leur sérieux et leur dévouement." },
                        { id: 2, name: "Essomba Binam Charles", date: "Avis vérifié ", rating: 5, url: "https://www.google.com/search?sa=X&sca_esv=4f696a57d58ffefe&sxsrf=ANbL-n7sBp4ZTXSX82B62xOwcomS4fuxtg:1777452283109&q=Hetep-Iaout+Services+Avis&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxIxNDUyMjcyMjC1MDAwMDczMjOysNzAyPiKUdIjtSS1QNczMb-0RCE4tagsMzm1WMGxLLN4EStuOQB3JQ-cUwAAAA&rldimm=15227220580007626289&tbm=lcl&hl=fr-FR&ved=2ahUKEwiamMLf1ZKUAxVxUqQEHSn1PAkQ9fQKegQIRBAG&biw=1536&bih=695&dpr=1.25#lkt=LocalPoiReviews", text: "Des auxiliaires de vie très bien formés et des repas de qualité. Merci His!" },
                        { id: 3, name: "Elisa Martinez", date: "Avis vérifié ", rating: 4, url: "https://www.google.com/search?sa=X&sca_esv=4f696a57d58ffefe&sxsrf=ANbL-n7sBp4ZTXSX82B62xOwcomS4fuxtg:1777452283109&q=Hetep-Iaout+Services+Avis&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxIxNDUyMjcyMjC1MDAwMDczMjOysNzAyPiKUdIjtSS1QNczMb-0RCE4tagsMzm1WMGxLLN4EStuOQB3JQ-cUwAAAA&rldimm=15227220580007626289&tbm=lcl&hl=fr-FR&ved=2ahUKEwiamMLf1ZKUAxVxUqQEHSn1PAkQ9fQKegQIRBAG&biw=1536&bih=695&dpr=1.25#lkt=LocalPoiReviews", text: "Cliente depuis longtemps la responsable qui s'occupe de moi connait bien mes attentes maintenant et m'envoie toujours du personnel de qualité." },
                    ]
                    .filter(avis => avis.rating >= 4)
                    .slice(0, 3)
                .map((avis, i) => (
                        <motion.a
                            key={avis.id}
                            href={avis.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.12 }}
                            whileHover={{ y: -4 }}
                            className={`group flex flex-col p-8 rounded-[2rem] shadow-sm hover:shadow-xl transition-shadow duration-300 border cursor-pointer ${isDark ? 'bg-gray-800/50 border-gray-700 hover:border-gray-500' : 'bg-white border-gray-100 hover:border-blue-200'}`}
                        >
                            <div className="flex items-center gap-4 mb-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm group-hover:bg-blue-600 group-hover:text-white transition-colors shrink-0">
                                    {avis.name.charAt(0)}
                                </div>
                                <div>
                                    <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>{avis.name}</p>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{avis.date}</p>
                                </div>
                                <FaGoogle className="ml-auto text-gray-300 dark:text-gray-600 opacity-50 group-hover:text-blue-500 group-hover:opacity-100 transition-all" size={16} />
                            </div>

                            <div className="flex gap-1 mb-4">
                                {[...Array(5)].map((_, i) => (
                                    <FaStar key={i} className={i < avis.rating ? "text-yellow-400" : "text-gray-200 dark:text-gray-600"} size={16} />
                                ))}
                            </div>

                            <p className={`text-sm leading-relaxed flex-1 italic ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                "{avis.text}"
                            </p>
                        </motion.a>
                    ))}
                </div>
            </motion.section>

            {/* --- FAQ CLINIQUE --- */}
            <motion.div className="py-12 md:py-24 px-4 md:px-6 max-w-3xl mx-auto" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}>
                <div className="text-center mb-8 md:mb-12">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-50 text-blue-600 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6"><FaQuestionCircle size={20} className="md:w-6 md:h-6" /></div>
                    <h2 className={`text-2xl md:text-4xl font-black mb-2 md:mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Questions Fréquentes</h2>
                </div>
                <div>
                    <FAQItem isDark={isDark} question="Puis-je bénéficier d'aides financières ?" answer="Oui, nos services donnent droit au crédit d'impôt de 50%. Selon la pathologie, vous pouvez bénéficier de l'APA ou de la PCH." />
                    <FAQItem isDark={isDark} question="Le personnel a-t-il une formation médicale ?" answer="Nos intervenants possèdent des diplômes d'État (DEAVS, AMP) et suivent des formations continues sur les pathologies lourdes." />
                    <FAQItem isDark={isDark} question="Que se passe-t-il en cas d'absence ?" answer="La continuity de service est notre priorité absolue. Nous garantissons un remplacement immédiat." />
                </div>
            </motion.div>

            {/* --- BLOGS --- */}
            <motion.div 
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
                className={`py-16 md:py-24 px-4 md:px-6 border-t ${isDark ? 'border-gray-800 bg-[#0a0f16]' : 'border-gray-100 bg-[#FAFCFF]'}`}
            >
                <div className="max-w-6xl mx-auto">
                    <div className="flex justify-center md:justify-between items-end mb-8 md:mb-12 text-center md:text-left">
                        <h2 className={`text-2xl md:text-4xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Actualités <br className="hidden md:block"/><span className="text-blue-600 font-light">Médico-sociales</span></h2>
                        <Link to="/blog" className="text-blue-600 font-bold hover:underline hidden md:block">Voir tout</Link>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {recentBlogs.length > 0 ? (
                            recentBlogs.map(blog => (
                                <Link key={blog.id} to={`/blog/${blog.id}`} className="block">
                                    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }} className={`rounded-3xl md:rounded-[2rem] overflow-hidden border transition-all hover:shadow-xl h-full ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                                        {blog.image ? (
                                            <img src={getBlogImageUrl(blog.image)} alt={blog.title} className="w-full h-48 md:h-56 object-cover" />
                                        ) : (
                                            <div className={`relative w-full h-48 md:h-56 overflow-hidden bg-gradient-to-br ${CARD_GRADIENTS[blog.id % CARD_GRADIENTS.length]}`}>
                                                <div className="absolute top-[-20px] right-[-20px] w-40 h-40 rounded-full bg-white/10"></div>
                                                <div className="absolute bottom-[-20px] left-[-20px] w-48 h-48 rounded-full bg-black/10"></div>
                                                <div className="absolute inset-0 flex items-center justify-center select-none pointer-events-none">
                                                    <span className="text-[7rem] font-black text-white/10 leading-none">
                                                        {(blog.title || '?').replace(/<[^>]+>/g, '').charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="p-6 md:p-8">
                                            <div className={`text-lg md:text-xl font-bold mb-4 md:mb-6 line-clamp-2 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}><SafeContent htmlContent={blog.title} /></div>
                                            <span className="text-blue-600 font-bold flex items-center gap-2 text-sm md:text-base">Lire l'article <FaArrowRight size={12}/></span>
                                        </div>
                                    </motion.div>
                                </Link>
                            ))
                        ) : (
                            <p className="text-center col-span-1 md:col-span-2 opacity-50 text-sm">Aucune actualité disponible.</p>
                        )}
                    </div>
                    <div className="mt-8 text-center md:hidden">
                        <Link to="/blog" className="text-blue-600 font-bold text-sm border border-blue-600 px-6 py-2 rounded-full">Voir tous les articles</Link>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

export default Home;